/**
 * Felhasználók és szerepek oldal — prototípus CSS
 */
import { useState, useEffect } from 'react';
import { get, post, del } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';

interface Role {
  id: string; name: string; description?: string; is_system: boolean; permissions: string[];
}

const ROLE_DISPLAY: Record<string, string> = {
  admin: 'Adminisztrátor', Adminisztrátor: 'Adminisztrátor',
  accountant: 'Könyvelő', Könyvelő: 'Könyvelő',
  financial: 'Pénzügyi vezető', Pénzügyes: 'Pénzügyi vezető',
  Munkatárs: 'Munkatárs', Vezető: 'Vezető',
};
function rd(name: string) { return ROLE_DISPLAY[name] || name; }

// permissions lehet tömb VAGY objektum {"module": ["read","write"]}
function countPerms(perms: any): number {
  if (!perms) return 0;
  if (Array.isArray(perms)) return perms.length;
  if (typeof perms === 'object') return Object.values(perms).reduce((s: number, v: any) => s + (Array.isArray(v) ? v.length : 0), 0);
  return 0;
}

function describePerms(perms: any): string {
  if (!perms) return 'Nincs jogosultság';
  if (Array.isArray(perms)) return `${perms.length} jogosultság`;
  if (typeof perms === 'object') {
    const keys = Object.keys(perms);
    if (keys.includes('*')) return 'Minden modul hozzáférés';
    return keys.map(k => k.replace('.*', '')).join(', ');
  }
  return '—';
}

export default function UsersPage() {
  const { toast } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPerms, setAllPerms] = useState<Array<{ code: string; module: string; description: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showNewRole, setShowNewRole] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, p] = await Promise.all([
          get<any>('/roles').catch(() => []),
          get<any>('/roles/permissions').catch(() => []),
        ]);
        setRoles(Array.isArray(r) ? r : r?.data || []);
        setAllPerms(Array.isArray(p) ? p : []);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  // Deduplikáljuk: ha két azonos nevű system role van, a nagyobb jogosultságút tartjuk
  const systemRolesRaw = roles.filter(r => r.is_system);
  const seenNames = new Map<string, Role>();
  for (const r of systemRolesRaw) {
    const displayName = rd(r.name);
    const existing = seenNames.get(displayName);
    if (!existing || countPerms(r.permissions) > countPerms(existing.permissions)) {
      seenNames.set(displayName, r);
    }
  }
  const systemRoles = Array.from(seenNames.values());
  const customRoles = roles.filter(r => !r.is_system);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>;

  return (
    <div>
      <PageHeader title="Felhasználók és szerepek" description={`${roles.length} szerep`}
        actions={<Button onClick={() => setShowInvite(true)}>+ Meghívás</Button>} />

      {/* Rendszer-szerepek */}
      <Card className="mb-4">
        <div className="card-h"><h3>Rendszer-szerepek</h3></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
          {systemRoles.map(r => (
            <div key={r.id} style={{ padding: '14px', background: 'var(--mist)', borderRadius: 'var(--r)', border: '1px solid var(--line)' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', marginBottom: '4px' }}>{rd(r.name)}</div>
              <div style={{ fontSize: '12px', color: 'var(--slate)', marginBottom: '6px' }}>{r.description || '—'}</div>
              <Badge>{describePerms(r.permissions)}</Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Egyedi szerepek */}
      <Card className="mb-4">
        <div className="card-h">
          <h3>Egyedi szerepek</h3>
          <Button size="sm" onClick={() => setShowNewRole(true)}>+ Új szerep</Button>
        </div>
        {customRoles.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--slate)', fontSize: '13px' }}>
            Még nincs egyedi szerep. A rendszer-szerepeken felül egyedi jogosultság-kombinációkat hozhatsz létre.
          </div>
        ) : (
          <div>
            {customRoles.map(r => (
              <div key={r.id} className="row-li">
                <div className="ri-main">
                  <div className="ri-t">{r.name}</div>
                  <div className="ri-s">{r.description || '—'} · {describePerms(r.permissions)}</div>
                </div>
                <Button size="sm" variant="ghost" style={{ color: 'var(--red)' }} onClick={async () => {
                  try { await del(`/roles/${r.id}`); setRoles(prev => prev.filter(x => x.id !== r.id)); toast('Szerep törölve.', 'success'); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                }}>Törlés</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Új szerep modal */}
      {showNewRole && <NewRoleModal
        allPerms={allPerms}
        onClose={() => setShowNewRole(false)}
        onSaved={(role) => { setRoles(prev => [...prev, role]); setShowNewRole(false); toast('Szerep létrehozva.', 'success'); }}
      />}

      {/* Meghívás modal */}
      {showInvite && <InviteModal roles={roles} onClose={() => setShowInvite(false)} onSent={() => { setShowInvite(false); toast('Meghívás elküldve.', 'success'); }} />}
    </div>
  );
}

function NewRoleModal({ allPerms, onClose, onSaved }: {
  allPerms: Array<{ code: string; module: string; description: string }>;
  onClose: () => void; onSaved: (role: Role) => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [perms, setPerms] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const permsByModule: Record<string, Array<{ code: string; description: string }>> = {};
  for (const p of allPerms) {
    if (!permsByModule[p.module]) permsByModule[p.module] = [];
    permsByModule[p.module].push(p);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const role = await post<any>('/roles', { name, permissions: perms });
      onSaved(role);
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Új egyedi szerep">
      <div className="space-y-4">
        <Input label="Szerep neve *" value={name} onChange={e => setName(e.target.value)} placeholder="pl. Értékesítő" />
        <div>
          <label className="form-label">Jogosultságok</label>
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: '8px' }}>
            {Object.entries(permsByModule).map(([module, modulePerms]) => (
              <div key={module} style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 700, fontSize: '12px', color: 'var(--navy)', textTransform: 'capitalize', marginBottom: '6px' }}>{module.replace(/_/g, ' ')}</div>
                {modulePerms.map(p => (
                  <label key={p.code} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '3px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={perms.includes(p.code)} onChange={e => {
                      if (e.target.checked) setPerms(prev => [...prev, p.code]);
                      else setPerms(prev => prev.filter(c => c !== p.code));
                    }} />
                    {p.description}
                  </label>
                ))}
              </div>
            ))}
            {allPerms.length === 0 && <div style={{ padding: '12px', color: 'var(--slate)', fontSize: '13px', textAlign: 'center' }}>Jogosultságok betöltése...</div>}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px' }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSave} loading={saving} disabled={!name}>Mentés</Button>
        </div>
      </div>
    </Modal>
  );
}

function InviteModal({ roles, onClose, onSent }: { roles: Role[]; onClose: () => void; onSent: () => void }) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState(roles[0]?.id || '');
  const [saving, setSaving] = useState(false);

  async function handleSend() {
    setSaving(true);
    try {
      await post('/team/invitations', { email, name: name || undefined, roleId: roleId || undefined });
      onSent();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Felhasználó meghívása">
      <div className="space-y-4">
        <Input label="E-mail cím *" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="felhasznalo@ceg.hu" />
        <Input label="Név" value={name} onChange={e => setName(e.target.value)} placeholder="Teljes név (opcionális)" />
        <div>
          <label className="form-label">Szerep</label>
          <select value={roleId} onChange={e => setRoleId(e.target.value)} className="form-input">
            {roles.map(r => <option key={r.id} value={r.id}>{rd(r.name)}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px' }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSend} loading={saving} disabled={!email}>Meghívás küldése</Button>
        </div>
      </div>
    </Modal>
  );
}
